import { useMemo } from 'react';
import { getSocColors } from '../../utils/socColor';
import {
  BATTERY_SECTION_COUNT,
  buildSocTierBatterySections,
  formatSocTierPriceLabel,
  SOC_TIER_BAND_FILL,
  socTierPriceBand,
} from '../../utils/socDynamicPriceNotes';
import './EmsBatteryIcon.css';

const SECTION_COUNT = BATTERY_SECTION_COUNT;

const TIER_BAND_FILL = SOC_TIER_BAND_FILL;

function getSectionFillPct(soc, sectionIndex) {
  const sectionSize = 100 / SECTION_COUNT;
  const sectionStart = sectionIndex * sectionSize;
  return Math.min(100, Math.max(0, ((soc - sectionStart) / sectionSize) * 100));
}

export default function EmsBatteryIcon({
  socPercent,
  charging = false,
  className = '',
  socPriceTiers = null,
  size = 'default',
}) {
  const soc = Math.min(100, Math.max(0, Number(socPercent) || 0));
  const colors = useMemo(() => getSocColors(socPercent), [socPercent]);
  const hasTierLabels = Array.isArray(socPriceTiers) && socPriceTiers.length > 0;
  const tierSections = useMemo(
    () => (hasTierLabels ? buildSocTierBatterySections(socPriceTiers, soc) : []),
    [hasTierLabels, socPriceTiers, soc]
  );

  const legacySections = useMemo(
    () =>
      Array.from({ length: SECTION_COUNT }, (_, index) => ({
        fillPct: getSectionFillPct(soc, index),
      })),
    [soc]
  );

  const rootClassName = [
    'ems-battery-icon',
    size === 'large' ? 'ems-battery-icon--large' : '',
    charging ? 'ems-battery-icon--charging' : '',
    hasTierLabels ? 'ems-battery-icon--tiered' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const iconBody = hasTierLabels ? (
    <>
      <div className="ems-battery-icon__cap" />
      <div className="ems-battery-icon__body ems-battery-icon__body--tiered">
        {tierSections.map((section, index) => {
          const band = socTierPriceBand(section.tier.priceUah, socPriceTiers);
          const fillColor = TIER_BAND_FILL[band] ?? TIER_BAND_FILL.avg;
          const priceLabel = formatSocTierPriceLabel(section.tier.priceUah);
          return (
            <div
              key={section.tier.percent}
              className={[
                'ems-battery-icon__section',
                'ems-battery-icon__section--tier',
                section.isActive ? 'ems-battery-icon__section--active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              style={{ flex: `${section.bandSpan} 1 0` }}
            >
              <div
                className={[
                  'ems-battery-icon__fill',
                  `ems-battery-icon__fill--${band}`,
                  section.isActive ? 'ems-battery-icon__fill--active-blink' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{
                  height: `${section.fillPct}%`,
                  background: fillColor,
                  transitionDelay: `${index * 45}ms`,
                }}
              />
              <span className="ems-battery-icon__price">{priceLabel} грн</span>
            </div>
          );
        })}
      </div>
    </>
  ) : (
    <>
      <div className="ems-battery-icon__cap" />
      <div className="ems-battery-icon__body">
        {legacySections.map((section, index) => {
          const isTopSection = index === SECTION_COUNT - 1;
          return (
            <div key={index} className="ems-battery-icon__section">
              <div
                className={[
                  'ems-battery-icon__fill',
                  charging && isTopSection ? 'ems-battery-icon__fill--charging-blink' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{
                  height: `${section.fillPct}%`,
                  transitionDelay: `${index * 45}ms`,
                }}
              />
            </div>
          );
        })}
      </div>
    </>
  );

  return (
    <div
      className={rootClassName}
      style={{
        '--ems-battery-color': colors.color,
        '--ems-battery-border': colors.border,
        '--ems-battery-empty': colors.fill,
      }}
      aria-hidden
    >
      {iconBody}
    </div>
  );
}
